import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem,
  FormControl, InputLabel, Select, RadioGroup, FormControlLabel, Radio,
  FormLabel, Alert, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Pagination,
  ToggleButton, ToggleButtonGroup, CircularProgress, IconButton,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  submitFeedback,
  fetchMyFeedback,
  ProductFeedback,
  FEEDBACK_ATTACHMENT_ACCEPT,
  FEEDBACK_ATTACHMENT_HELP_TEXT,
} from '../../functions/productFeedbackApi';
import TicketDetailModal from './TicketDetailModal';
import MarkdownDescriptionField from './MarkdownDescriptionField';
import { useSessionState } from '../../hooks/useSessionState';

const TYPE_COLORS: Record<string, { main: string; bg: string }> = {
  bug:         { main: '#B91C1C', bg: '#FEE2E2' },
  enhancement: { main: '#6D28D9', bg: '#EDE9FE' }, // violet — distinct from warning
  feature:     { main: '#1D4ED8', bg: '#DBEAFE' },
};

const PRIORITY_COLORS: Record<string, { main: string; bg: string }> = {
  high:   { main: '#BE123C', bg: '#FFE4E6' }, // rose — urgent, not amber warning
  medium: { main: '#475569', bg: '#E2E8F0' }, // slate — neutral
  low:    { main: '#047857', bg: '#D1FAE5' },
};

const STATUS_COLORS: Record<string, { main: string; bg: string }> = {
  open:   { main: '#4338CA', bg: '#E0E7FF' }, // indigo — active, not warning
  solved: { main: '#065F46', bg: '#D1FAE5' },
};

const TYPE_LABELS: Record<string, string> = {
  bug: 'Bug / Issue',
  enhancement: 'Enhancement',
  feature: 'New Feature',
};

const ITEMS_PER_PAGE = 10;

const ProvideFeedbackTab: React.FC = () => {
  // Form state — text/select fields persist as a draft (sessionStorage) so navigating away
  // (or refreshing) and coming back restores them. Attachments can't be persisted this way
  // (File objects aren't serializable, and storage is size-capped) — those still reset.
  const [type, setType] = useSessionState('feedback:draft:type', '');
  const [title, setTitle] = useSessionState('feedback:draft:title', '');
  const [description, setDescription] = useSessionState('feedback:draft:description', '');
  const [priority, setPriority] = useSessionState('feedback:draft:priority', '');
  const [section, setSection] = useSessionState('feedback:draft:section', '');
  const [affectedUrl, setAffectedUrl] = useSessionState('feedback:draft:affectedUrl', '');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [debounced, setDebounced] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [warningMsg, setWarningMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // List state
  const [tickets, setTickets] = useState<ProductFeedback[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'solved'>('all');
  const [loadingList, setLoadingList] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<ProductFeedback | null>(null);

  const loadTickets = useCallback(async (p: number) => {
    setLoadingList(true);
    try {
      const resp = await fetchMyFeedback(p, ITEMS_PER_PAGE);
      setTickets(resp.data);
      setTotal(resp.total);
    } catch {
      // silently fail — list just stays empty
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => { loadTickets(1); }, [loadTickets]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setImageFiles(prev => [...prev, ...files]);
    // Reset input so same file can be re-added after removal
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
  };

  const resetForm = () => {
    setType('');
    setTitle('');
    setDescription('');
    setPriority('');
    setSection('');
    setAffectedUrl('');
    setImageFiles([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!type || !title.trim() || !description.trim()) {
      setErrorMsg('Type, title, and description are required.');
      return;
    }

    setSubmitting(true);
    setDebounced(true);
    setErrorMsg('');
    setSuccessMsg('');
    setWarningMsg('');

    // 2-second debounce on button
    setTimeout(() => setDebounced(false), 2000);

    const formData = new FormData();
    formData.append('type', type);
    formData.append('title', title.trim());
    formData.append('description', description.trim());
    if (priority) formData.append('priority', priority);
    if (section.trim()) formData.append('section', section.trim());
    if (affectedUrl.trim()) formData.append('affectedUrl', affectedUrl.trim());
    imageFiles.forEach(file => formData.append('images', file));

    try {
      const resp: any = await submitFeedback(formData);
      if (resp?.success) {
        setSuccessMsg(`Submitted! Your ticket ID is ${resp.data.ticketId}.`);
        if (resp.attachmentFailures?.length) {
          const list = resp.attachmentFailures.map((f: any) => `"${f.name}" (${f.reason})`).join(', ');
          setWarningMsg(`Ticket saved, but this didn't sync to our engineering tracker: ${list}. It's still attached to your ticket — edit the ticket to retry if needed.`);
        }
        // Optimistic prepend
        setTickets(prev => [resp.data, ...prev]);
        setTotal(prev => prev + 1);
        resetForm();
      } else {
        setErrorMsg(resp?.message || 'Submission failed. Please try again.');
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    loadTickets(value);
  };

  const filteredTickets = statusFilter === 'all'
    ? tickets
    : tickets.filter(t => t.status === statusFilter);

  return (
    <Box sx={{ p: 3 }}>

      {/* ── Submission Form ── */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Submit Feedback</Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 680 }}>
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
        )}
        {warningMsg && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarningMsg('')}>{warningMsg}</Alert>
        )}
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMsg('')}>{errorMsg}</Alert>
        )}

        <FormControl fullWidth required variant="outlined" sx={{ mb: 2 }}>
          <InputLabel
            id="feedback-type-label"
            sx={{
              '&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink)': {
                transform: 'translate(14px, 9px) scale(1)',
              },
            }}
          >
            Type
          </InputLabel>
          <Select
            labelId="feedback-type-label"
            id="feedback-type"
            label="Type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            sx={{
              // Match outlined TextField vertical rhythm; keep value vertically centered
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                minHeight: 23,
                lineHeight: 1.4375,
                py: '16.5px',
                pl: '14px',
                pr: '32px',
                boxSizing: 'content-box',
              },
            }}
          >
            <MenuItem value="bug">Bug / Issue</MenuItem>
            <MenuItem value="enhancement">Enhancement</MenuItem>
            <MenuItem value="feature">New Feature</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth label="Title" value={title}
          onChange={e => setTitle(e.target.value)}
          required sx={{ mb: 2 }}
        />

        <MarkdownDescriptionField
          value={description}
          onChange={setDescription}
          required
        />

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend" sx={{ fontSize: '13px' }}>Priority (optional)</FormLabel>
          <RadioGroup row value={priority} onChange={e => setPriority(e.target.value)}>
            <FormControlLabel value="high"   control={<Radio size="small" />} label="High" />
            <FormControlLabel value="medium" control={<Radio size="small" />} label="Medium" />
            <FormControlLabel value="low"    control={<Radio size="small" />} label="Low" />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth label="Section / Screen (optional)" value={section}
          onChange={e => setSection(e.target.value)} sx={{ mb: 2 }}
        />

        <TextField
          fullWidth label="Affected URL (optional)" value={affectedUrl}
          onChange={e => setAffectedUrl(e.target.value)} sx={{ mb: 2 }}
          placeholder="https://..."
        />

                {/* Attachment picker */}
        <input
          ref={fileInputRef}
          type="file"
          accept={FEEDBACK_ATTACHMENT_ACCEPT}
          multiple
          style={{ display: 'none' }}
          onChange={handleImageChange}
        />
        <Box sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', display: 'block', mb: 0.5 }}>
            Attachments (optional)
          </Typography>
          {imageFiles.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
              {imageFiles.map((file, i) => (
                <Box
                  key={`${file.name}-${i}`}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start',
                    px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    maxWidth: '100%',
                  }}
                >
                  <InsertDriveFileOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Typography variant="body2" noWrap sx={{ maxWidth: 320 }}>{file.name}</Typography>
                  <IconButton
                    type="button" size="small" aria-label={`Remove ${file.name}`}
                    onClick={() => removeImage(i)}
                    sx={{ width: 22, height: 22, ml: 0.5 }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}

          <Button
            type="button"
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Attach Files
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
            {FEEDBACK_ATTACHMENT_HELP_TEXT}
          </Typography>
        </Box>

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || debounced}
        >
          {submitting ? 'Submitting…' : 'Submit Feedback'}
        </Button>
      </Box>

      {/* ── My Submissions ── */}
      <Box sx={{ mt: 5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>My Submissions</Typography>

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, val) => { if (val) setStatusFilter(val); }}
            size="small"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="open">Open</ToggleButton>
            <ToggleButton value="solved">Resolved</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loadingList ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredTickets.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, color: '#aaa' }}>
            <Typography>No submissions found.</Typography>
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F5F7F9' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Ticket ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.map(ticket => {
                    const tc = TYPE_COLORS[ticket.type]     || { main: '#1976d2', bg: '#F5F7F9' };
                    const pc = ticket.priority ? PRIORITY_COLORS[ticket.priority] : null;
                    const sc = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                    return (
                      <TableRow
                        key={ticket._id}
                        hover
                        onClick={() => setSelectedTicket(ticket)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{ticket.ticketId}</TableCell>
                        <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {ticket.title}
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: tc.bg, border: `1px solid ${tc.main}33`, fontSize: '12px', fontWeight: 500, color: tc.main, whiteSpace: 'nowrap' }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: tc.main, flexShrink: 0 }} />
                                {TYPE_LABELS[ticket.type] || ticket.type}
                            </Box>
                        </TableCell>
                        <TableCell>
                            {pc && ticket.priority ? (
                                <TableCell>
                                {pc && ticket.priority ? (
                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: pc.bg, border: `1px solid ${pc.main}33`, fontSize: '12px', fontWeight: 500, color: pc.main, whiteSpace: 'nowrap' }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: pc.main, flexShrink: 0 }} />
                                    {ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)}
                                    </Box>
                                ) : (
                                    <Box sx={{ pl: '4px', color: '#aaa', fontSize: '13px' }}>—</Box>
                                )}
                                </TableCell>
                            ) : (
                                <Box sx={{ pl: '4px', color: '#aaa', fontSize: '13px' }}>—</Box>
                            )}
                        </TableCell>
                        <TableCell>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '8px', height: '22px', borderRadius: '4px', backgroundColor: sc.bg, border: `1px solid ${sc.main}33`, fontSize: '12px', fontWeight: 500, color: sc.main, whiteSpace: 'nowrap' }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: sc.main, flexShrink: 0 }} />
                                {ticket.status === 'solved' ? 'Solved' : 'Open'}
                            </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {new Date(ticket.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {total > ITEMS_PER_PAGE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={Math.ceil(total / ITEMS_PER_PAGE)}
                  page={page}
                  onChange={handlePageChange}
                  size="small"
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <TicketDetailModal
        ticket={selectedTicket}
        open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        isAdmin={false}
        canEdit
        onUpdated={(updated, attachmentFailures) => {
          setTickets(prev => prev.map(t => t._id === updated._id ? updated : t));
          setSelectedTicket(updated);
          if (attachmentFailures?.length) {
            const list = attachmentFailures.map(f => `"${f.name}" (${f.reason})`).join(', ');
            setWarningMsg(`Ticket updated, but this didn't sync to our engineering tracker: ${list}. It's still attached to your ticket — edit again to retry if needed.`);
          }
        }}
      />
    </Box>
  );
};

export default ProvideFeedbackTab;